uniform float uTime;
uniform sampler2D uTexture;
uniform vec2 uTextureSize;
uniform bool uIsLocal;
uniform float uStopEmitTime;
uniform vec3 uAcceleration;

// Particles can have size gradient over lifetime defined via texture
uniform sampler2D uSizes;
uniform lowp int uSizesCount;
uniform sampler2D uSizeStops;
uniform lowp int uSizeStopCount;

// Optional sprite sheet animation properties
uniform bool uIsAnimationSheetEnabled;
uniform lowp int uStartSpriteCellId;
uniform lowp int uEndSpriteCellId;
uniform float uSpriteCellChangeSpeed;
uniform float uSpriteCellWidth;
uniform float uSpriteCellHeight;
uniform bool uSpriteRandomStartCell;

// Packed timing and size data
attribute vec4 aTimingAndSize; // (index, emitTime, lifetime, size)
// Packed motion data
attribute vec4 aMotion; // (direction.xyz, angularSpeed)
// Packed position and rotation data
attribute vec4 aPosAndRotation; // (position.xyz, zRotation)

// More attributes, not packed because we still have space for them
attribute vec4 instanceColor;
attribute vec3 instanceEmitWorldPosition;

varying float vIndex;
varying float vLifetime;
varying float vAge;
varying float vAgeFade;
varying vec4 vColor;
varying vec2 vUV; // case sensitive, because babylon shaders rely on this property
varying vec2 vSpriteUvOffset;
varying vec2 vSpriteUvScale;
varying vec4 vWorldPos;

// Every particle's size gradient is stored in the texture sequentially, where
// a pixel represents a size value. For example, [0,0] represents the first
// particle's first size stop. If we know each pixel has 3 size stops, then
// [1,0] represents the first particle's second size stop, and [3,0] represents
// the first particle's third size stop. [4,0] would then start the second
// particle's size sequence. The texture wraps after 512 pixels.
ivec2 sizeIndexToTexel(lowp int index) {
    int width = 512;
    int x = index % width;
    int y = index / width;
    return ivec2(x, y);
}

// Similar to above, but instead of each pixel representing a size value, it
// represents the percentage stop (0.0 to 1.0) in the gradient.
ivec2 sizesStopIndexToTexel(lowp int index) {
    return ivec2(index, 0);
}

void main() {
    // Unpack timing and size data
    float instanceIndex = aTimingAndSize.x;
    float instanceEmitTime = aTimingAndSize.y;
    float instanceLifetime = aTimingAndSize.z;
    float instanceSize = aTimingAndSize.w;

    // Unpack motion data
    vec3 instanceDirection = aMotion.xyz;
    float instanceAngularSpeed = aMotion.w;

    // Unpack position and rotation data
    vec3 instancePosition = aPosAndRotation.xyz;
    float instanceZRotation = aPosAndRotation.w;

    vIndex = instanceIndex; // index from 0,n where n is the capacity of the particle system
    vAge = (uTime - instanceEmitTime); // the current particle's age
    vLifetime = instanceLifetime;
    vColor = instanceColor;
    vUV = uv;

    // Enable lifetime culling: hide particles that haven't been emitted yet or are dead
    if(vAge < 0.0 || vAge > vLifetime) {
        return;
    }

    // Stop emitting new particles after uStopEmitTime
    // Only show particles that were emitted before the stop time
    if(uStopEmitTime > 0.0 && instanceEmitTime > uStopEmitTime) {
        return;
    }

    // Calculate sprite cell animation
    if(uIsAnimationSheetEnabled && uSpriteCellWidth > 0.0 && uSpriteCellHeight > 0.0) {
        int columns = int(uTextureSize.x / uSpriteCellWidth);
        int rows = int(uTextureSize.y / uSpriteCellHeight);

        float columnWidth = 1.0 / float(columns);
        float rowHeight = 1.0 / float(rows);

        // Add small padding to prevent texture bleeding from adjacent cells
        float padding = 0.5 / uTextureSize.x; // Half pixel padding in UV space
        float paddingV = 0.5 / uTextureSize.y;

        lowp int totalCells = uEndSpriteCellId - uStartSpriteCellId; // n - 1
        float cellProgress = vAge / vLifetime * uSpriteCellChangeSpeed;

        int currentCell = (totalCells) - int(cellProgress * float(totalCells)); // n to 0 (reversed)

        int column = currentCell % columns; // 0 to columns - 1
        int row = currentCell / columns; // 0 to rows - 1

        // Apply padding to scale and offset to inset UV coordinates
        vSpriteUvScale = vec2(columnWidth - padding * 2.0, rowHeight - paddingV * 2.0);
        vSpriteUvOffset = vec2(float(column) * columnWidth + padding, float(row) * rowHeight + paddingV);
    } else {
        vSpriteUvScale = vec2(1.0, 1.0);
        vSpriteUvOffset = vec2(0.0, 0.0);
    }
        
    // Transform to view space
    vec4 mvPosition;
    vec3 directionOverTime = instanceDirection * vAge;
    vec3 accelerationOverTime = 0.5 * uAcceleration * vAge * vAge;
    if (uIsLocal) {
        // Local-space: particles follow the parent mesh
        vec3 particlePosition = instancePosition + directionOverTime + accelerationOverTime;
        mvPosition = viewMatrix * modelMatrix * vec4(particlePosition, 1.0);
    } else {
        // World-space: particles start where they originally emitted
        vec3 particlePosition = instanceEmitWorldPosition + directionOverTime + accelerationOverTime;
        mvPosition = viewMatrix * vec4(particlePosition, 1.0);
    }
    
    // Calculate size based on size over lifetime if applicable
    lowp int particle = int(vIndex);
    float progress = vAge / vLifetime;

    // Fade in/out based on lifetime progress (used by default and custom shaders)
    float fadeIn = smoothstep(0.0, 0.1, progress);  // Fade in during first 10% of lifetime
    float fadeOut = smoothstep(1.0, 0.8, progress); // Fade out during last 20% of lifetime
    vAgeFade = fadeIn * fadeOut;

    float size = instanceSize;
    if(uSizeStopCount > 0) {
        lowp int leftBound = 0;
        lowp int rightBound = 0;

        for(lowp int i = 0; i < uSizeStopCount; i++) {
            float stop = texelFetch(uSizeStops, sizesStopIndexToTexel(i), 0).r;
            if(progress >= stop) {
                leftBound = i;
            }
        }
        rightBound = min(leftBound + 1, uSizeStopCount - 1);

        lowp int uvIndex = particle * uSizeStopCount;

        float sizeA = texelFetch(uSizes, sizeIndexToTexel(uvIndex + leftBound), 0).r;
        float sizeB = texelFetch(uSizes, sizeIndexToTexel(uvIndex + rightBound), 0).r;

        float stopA = texelFetch(uSizeStops, sizesStopIndexToTexel(leftBound), 0).r;
        float stopB = texelFetch(uSizeStops, sizesStopIndexToTexel(rightBound), 0).r;
        if(stopA == stopB) {
            size = sizeA;
        } else {
            float localProgress = (progress - stopA) / (stopB - stopA);
            size = mix(sizeA, sizeB, localProgress);
        }
    }
    
    // Billboard the quad to face the camera with actual size
    // Apply initial rotation plus continuous rotation from angular speed
    float angle = instanceZRotation + instanceAngularSpeed * vAge;
    float cosA = cos(angle);
    float sinA = sin(angle);
    
    // Rotate the position in the xy plane (local z-axis rotation)
    vec2 rotatedPos = vec2(
        position.x * cosA - position.y * sinA,
        position.x * sinA + position.y * cosA
    );
    
    mvPosition.xy += rotatedPos * size * 5.0;
    
    gl_Position = projectionMatrix * mvPosition;
    vWorldPos = gl_Position;
}
